import { Button, TableItem } from 'components/atoms';
import { MagellanPopups } from 'components/molecules';
import SendButtonEmail from 'components/molecules/buttons/sendEmail/SendButtonEmail';
import LabeledEmailSend from 'components/molecules/labeledStatus/LabeledEmailSend';
import DownloadFilesInPeriodPopup from 'components/molecules/popup/download-files-in-period/DownloadFilesInPeriodPopup';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiDownload } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { DOCUMENT_TYPE } from 'services/email/types';
import {
  useDeleteSettlement,
  useGetSettlementFile,
  useGetSettlementFiles,
  useGetSettlementInfinite,
  useGetSettlementStatistics,
} from 'services/settlement';
import { ISettlementResponse } from 'services/settlement/types';
import { EMAIL_SEND } from 'services/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';

enum SORT_COLUMNS {
  startSettlementPeriod,
  endSettlementPeriod,
  label,
  settlementNumber,
  net,
}

const Settlement = () => {
  usePermissionGuard('SettlementPermissionsLIST**', '/', true, 'statements');

  const { t } = useTranslation();
  const i18nextPrefix = 'settlement';

  const columnsVisibility = useTableColumnsVisibility(
    'SettlementListEmail',
    SORT_COLUMNS
  );

  const settlements = useGetSettlementInfinite({});
  const { mutateAsync: deleteSettlement } = useDeleteSettlement();

  const { setFile } = useStreamFileDownload(useGetSettlementFile);

  const tableProps = useInfiniteTable(settlements, { selectable: false });

  const statistics = useGetSettlementStatistics();

  const netAmountLength = formatCurrency(statistics?.data?.allNetAmount).length;

  const handleDelete = async (
    settlementId: string,
    actions: PopupActions | null
  ) => {
    await deleteSettlement(
      { settlementId },
      {
        onSuccess: () => {
          settlements.refetch();
          actions?.close();
        },
      }
    );
  };
  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <DownloadFilesInPeriodPopup
                service={useGetSettlementFiles}
                filePrefix={t('navbar.settlement')}
              />
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix='input.label'
                customI18next=''
              />
            </>
          }
        />
      }>
      <FooterTable
        {...tableProps}
        id={columnsVisibility.tableId}
        columns={[
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(startSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
            footer: t('table.total'),
          },
          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriodName }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(endSettlementPeriodName)}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          {
            label: t('input.label.settlementNumber'),
            renderCell: ({ settlementNumber }) => (
              <TableItem>
                <TableItem.Text text={settlementNumber} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.settlementNumber),
          },
          {
            label: t('input.label.net'),
            renderCell: (item: ISettlementResponse) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(item.netAmount, {
                    length: netAmountLength,
                  })}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.net),
            footer: formatCurrency(statistics.data?.allNetAmount),
          },
          {
            label: t('input.label.email-sender'),
            renderCell: ({ id, sendStatus }) => (
              <TableItem>
                <div className='flex items-center gap-2'>
                  {sendStatus !== EMAIL_SEND.notSend && (
                    <LabeledEmailSend status={sendStatus} />
                  )}
                  {sendStatus !== EMAIL_SEND.planned && (
                    <SendButtonEmail
                      documentId={id}
                      documentType={DOCUMENT_TYPE.settlement}
                      refetch={settlements.refetch}
                      status={sendStatus}
                    />
                  )}
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: '',
            renderCell: (item: ISettlementResponse) => (
              <TableItem justifyPosition='end'>
                {
                  <div className='flex'>
                    <Button
                      type='button'
                      isClear
                      onlyIcon
                      disabled={!item.filePath}
                      onClick={() =>
                        setFile({ id: item.id, name: item.settlementNumber })
                      }>
                      <Button.Icon icon={<PiDownload />} />
                      {/* <Button.Text text={t('button.download')} /> */}
                    </Button>
                    <MagellanPopups.DeleteWarning
                      name={`${t('settlement.what')} ${item.settlementNumber} ${
                        item.labelName ?? ''
                      } `}
                      onRemove={actions => handleDelete(item.id, actions)}
                    />
                    {/* <Button
                      type='button'
                      isClear
                      onClick={() => handleDelete(item.id)}
                      onlyIcon
                      variant={BUTTON_VARIANT.remove}>
                      <Button.Icon icon={<PiTrash />} />
                    </Button> */}
                  </div>
                }
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Settlement;
